import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  MobileBundleCard,
  MobileBundleCardInfo,
} from 'src/components/reusable-components/mobile-bundle-card/MobileBundleCard';
import { BundleMessages } from 'src/intl/BundleMessages';
import { Bundle } from 'src/models/bundle';
import { useBundleActions } from 'src/pages/bundles/useBundleActions';
import { BleConnectionState } from 'src/redux/ble/constants';
import { selectBleConnectionState, selectBleIsLocked } from 'src/redux/ble/selectors';
import { isValidBundlePattern } from 'src/utils/helpers/bundleParserHelpers';
import { useSocketGet } from 'src/utils/socket';
import { SocketContextSentinel } from 'src/utils/socket/context/sentinelSocketContext';

interface BundlesListInfiniteItemProps {
  bundle: Bundle;
}

export const BundlesListInfiniteItem: React.FC<BundlesListInfiniteItemProps> = ({ bundle }) => {
  const routerLink = `bundles/${bundle._id}`;
  const { formatMessage } = useIntl();
  const { writeBundle, deleteBundle, installedBundleFile } = useBundleActions(bundle);
  const isBleLocked = useSelector(selectBleIsLocked);
  const isConnected = useSelector(selectBleConnectionState) === BleConnectionState.CONNECTED;
  const isActionDisabled = isBleLocked || !isConnected;
  const { item: bundleOrg } = useSocketGet(SocketContextSentinel, 'organizations', bundle.organizationId);

  const onPress = installedBundleFile ? deleteBundle : writeBundle;
  const action = useMemo(() => {
    const result: MobileBundleCardInfo['action'] = {
      text: installedBundleFile
        ? formatMessage(BundleMessages.uninstallButton)
        : formatMessage(BundleMessages.installButton),
      color: installedBundleFile ? 'secondary' : 'primary',
      onPress,
      disabled: isActionDisabled,
    };
    return result;
  }, [formatMessage, isActionDisabled, installedBundleFile, onPress]);

  const tubes = isValidBundlePattern(bundle.pattern)
    ? { count: bundle.tubes.length, pattern: bundle.pattern }
    : undefined;

  return (
    <div className="ion-padding-top">
      <MobileBundleCard
        routerLink={routerLink}
        bundleName={bundle.name}
        siteName={bundle.siteName}
        orgName={bundleOrg?.orgName}
        tubes={tubes}
        model={bundle.heatExchangerModelNumber ?? undefined}
        createdAt={bundle.createdAt}
        action={action}
      />
    </div>
  );
};
